import { useState } from "react";
import { X } from "lucide-react";
import { LoadingSpinner } from "../LoadingSpinner";
import { useClickOutside } from '../../hooks/useClickOutside';
import useEscapeKey from "../../hooks/useEscapeKey";
import { motion } from "framer-motion";
import ValidatedInput from "../shared/ValidatedInput";
import { DEFAULT_VALIDATION_CONFIG, validateAndSanitizeInput } from "../../utils/validation/inputValidation";

interface SaveProjectDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (name: string, description: string) => void;
  initialName?: string;
  headerText?: string;
  isLoading?: boolean;
}

export function SaveProjectDialog({ 
  open, 
  onClose, 
  onSave, 
  initialName = 'Untitled Project', 
  headerText = 'Save Project',
  isLoading = false 
}: SaveProjectDialogProps) {
  const [name, setName] = useState(validateAndSanitizeInput(initialName, DEFAULT_VALIDATION_CONFIG.projectName));
  const [description, setDescription] = useState('');
  const [nameError, setNameError] = useState('');
  
  const dialogRef = useClickOutside<HTMLDivElement>(onClose);
  useEscapeKey(onClose); // Use escape key to close the dialog

  // Don't render if not open
  if (!open) return null;

  const handleSave = () => {
    if (!name.trim()) {
      setNameError('Project name is required');
      return;
    }
    
    if (!isLoading) {
      const sanitizedName = validateAndSanitizeInput(name, DEFAULT_VALIDATION_CONFIG.projectName);
      const sanitizedDescription = validateAndSanitizeInput(description, DEFAULT_VALIDATION_CONFIG.projectDescription);
      
      onSave(sanitizedName, sanitizedDescription);
      onClose(); // Close the dialog after saving
    }
  };

  return (
    <motion.div 
      className="fixed inset-0 bg-black/50 dark:bg-gray-950/70 flex items-center justify-center z-[200]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div 
        ref={dialogRef} 
        className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-lg mx-4 border border-gray-200 dark:border-gray-700"
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
      >
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 px-6 pt-5 rounded-t-lg">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{headerText}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-300 dark:text-gray-500 dark:hover:text-gray-400">
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-2">
        <div className="space-y-4">
          <ValidatedInput
            id="name"
            label="Project Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError('');
            }}
            onKeyDown={(e) => { // Handle Enter key for submission
              if (e.key === 'Enter' && name.trim() && !isLoading) {
                e.preventDefault();
                handleSave();
              }
            }}
            validationConfig={DEFAULT_VALIDATION_CONFIG.projectName}
            placeholder="Enter project name..."
            autoFocus={true}
            errorMessage={nameError}
            onBlur={() => {
              if (!name.trim()) {
                setNameError('Project name is required');
              }
            }}
          />
          
          <ValidatedInput
            id="description"
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onKeyDown={(e) => { // Handle Enter key for submission (if needed for textarea, though usually not)
              if (e.key === 'Enter' && !e.shiftKey && name.trim() && !isLoading) { // Allow Shift+Enter
                e.preventDefault();
                handleSave();
              }
            }}
            validationConfig={DEFAULT_VALIDATION_CONFIG.projectDescription}
            placeholder="Describe your project..."
            isTextarea={true}
            rows={4}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="p-6 rounded-b-lg">
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {/* {name.trim() ? 'Ready to save' : 'Enter a project name to continue'} */}
          </p>
          <div className="flex gap-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!name.trim() || isLoading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <LoadingSpinner size="sm" className="text-white" />
                  <span>Saving...</span>
                </>
              ) : (
                <span>Save Project</span>
              )}
            </button>
          </div>
        </div>
      </div>
      </motion.div>
    </motion.div>
  );
}
